// SPDX-License-Identifier: Apache-2.0

// PresenceBar — Solid component that renders a stack of peer avatars plus a
// "+N more" overflow chip. Hover any avatar to see name + cursor location.
//
// Port of v1's `presence-bar.tsx`. Pure visual; all state comes from the
// `peers` accessor — never mutates.

import { For, Show, type Accessor } from "solid-js";
import type { PeerPresence } from "./use-presence";
import "./collab.css";

const VISIBLE_LIMIT = 5;

export interface PresenceBarProps {
  peers: Accessor<PeerPresence[]>;
  /** Optional class name applied to the wrapper for layout-level overrides. */
  class?: string;
}

export function PresenceBar(props: PresenceBarProps) {
  const visible = () => props.peers().slice(0, VISIBLE_LIMIT);
  const overflow = () => Math.max(0, props.peers().length - VISIBLE_LIMIT);

  return (
    <div
      class={`mv-presence ${props.class ?? ""}`.trim()}
      data-testid="presence-bar"
      data-peer-count={props.peers().length}
      aria-label={`${props.peers().length} other ${
        props.peers().length === 1 ? "person" : "people"
      } in this room`}
    >
      <Show when={props.peers().length === 0}>
        <span class="mv-presence__empty" aria-hidden="true">
          solo
        </span>
      </Show>
      <Show when={props.peers().length > 0}>
        <div class="mv-presence__stack" role="list">
          <For each={visible()}>
            {(peer, i) => (
              <PresenceAvatar peer={peer} index={i()} />
            )}
          </For>
        </div>
        <Show when={overflow() > 0}>
          <span class="mv-presence__more" data-testid="presence-overflow">
            +{overflow()} more
          </span>
        </Show>
      </Show>
    </div>
  );
}

interface PresenceAvatarProps {
  peer: PeerPresence;
  index: number;
}

function PresenceAvatar(props: PresenceAvatarProps) {
  const initials = () => {
    const n = props.peer.name?.trim() ?? "";
    if (n.length === 0) return "?";
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  };
  const tooltip = () => {
    const c = props.peer.cursor;
    if (!c) return props.peer.name;
    return `${props.peer.name} · ${c.docId}@${c.ch}`;
  };
  return (
    <span
      class="mv-presence__avatar"
      role="listitem"
      title={tooltip()}
      style={{
        "background-color": props.peer.color,
        // Stack overlap: each subsequent avatar shifts left by 30% of width.
        "margin-left": props.index === 0 ? "0" : "-9px",
        "z-index": 100 - props.index,
      }}
      data-client-id={props.peer.clientId}
      data-testid={`presence-avatar-${props.peer.clientId}`}
    >
      <span class="mv-presence__initials" aria-hidden="true">
        {initials()}
      </span>
    </span>
  );
}

export default PresenceBar;
